import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import Table from "../../components/Tables/Table";
import { Drawer, Form, Modal, Select, Space, Tag, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";
import { getInstitutions } from "../../services/institutionService";
import CustomDropDownInput from "../../components/Inputs/CustomDropDownInput";
import { format, subYears } from "date-fns";

import {
  createAccreditation,
  deleteAccreditation,
  getAccreditations,
  updateAccreditation,
} from "../../services/accreditationsService";
import { accreditationSchema } from "../../validators/accreditationValidators";
import { getAccreditingBodies } from "../../services/accreditingBody";
import DateInput from "../../components/Inputs/DateInput";

import { BiShow } from "react-icons/bi";
import { useStateContext } from "../../context/ContextProvider";
import { isInstitutionAdmin } from "../../utils/authHelpers";

const Accreditations = () => {
  const [accreditations, setAccreditattions] = useState([]);
  const [filteredAccreditations, setFilteredAccreditations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [accreditationToEdit, setAccreditationToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [accreditationToDelete, setAccereditationToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [accreditingBodies, setAccreditingBodies] = useState([]);
  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");
  const [selectedAccreditingBody, setSelectedAccreditingBody] =
    useState("Not Selected");

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedAccreditation, setSelectedAccreditation] = useState(null);

  const { user } = useStateContext();
  const isInstAdmin = isInstitutionAdmin(user);

  useEffect(() => {
    loadAccreditations();
    loadInstitutions();
    loadAccreditingBodies();
  }, []);

  useEffect(() => {
    let filtered = accreditations;
    if (searchTerm.trim() !== "") {
      filtered = accreditations.filter((campus) =>
        `${campus.location} ${campus?.institution?.name || ""}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution?.toString()
      );
    }

    if (selectedAccreditingBody !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.accrediting_body?.id?.toString() ===
          selectedAccreditingBody?.toString()
      );
    }

    setFilteredAccreditations(filtered);
  }, [
    searchTerm,
    accreditations,
    selectedInstitution,
    selectedAccreditingBody,
  ]);

  const loadAccreditations = async () => {
    setLoading(true);
    try {
      const { data } = await getAccreditations();
      setAccreditattions(data.data);
      setFilteredAccreditations(data.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Failed to load Accreditations"
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (accreditationToEdit) {
      onUpdateAccreditation(payload, resetForm);
    } else {
      onCreateAccreditation(payload, resetForm);
    }
  };

  const loadInstitutions = async () => {
    setLoading(true);
    try {
      const { data } = await getInstitutions();
      setInstitutions(data.data);
    } catch (error) {
      console.log(error);

      if (error) {
        toast.error(
          error?.response?.data?.error || "Failed to load Institutions"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAccreditingBodies = async () => {
    setLoading(true);
    try {
      const { data } = await getAccreditingBodies();
      setAccreditingBodies(data.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Failed to load Accrediting Bodies"
      );
    } finally {
      setLoading(false);
    }
  };

  const onCreateAccreditation = async (payload, resetForm) => {
    setErrs(null);

    try {
      const { data } = await createAccreditation(payload);

      setAccreditattions((prev) => [...prev, data.data]);

      toast.success("Accreditation created successfully");

      setIsModalOpen(false);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error || "Accreditation was not created, try again!"
        );
      }
    }
  };

  const handleEdit = (accreditation) => {
    setAccreditationToEdit(accreditation);
    setIsModalOpen(true);
  };

  const onUpdateAccreditation = async (payload, resetForm) => {
    setErrs(null);

    const formData = new FormData();

    formData.append("_method", "PATCH");
    formData.append("name", payload.name);
    formData.append("membership_status", payload.membership_status);
    formData.append("expiry_date", payload.expiry_date);
    formData.append("date_awarded", payload.date_awarded);
    formData.append("accrediting_body_id", payload.accrediting_body_id);
    formData.append("institution_id", payload.institution_id);

    try {
      const originalAccreditations = [...accreditations];

      const accreditationIndex = originalAccreditations.findIndex(
        (accreditation) => accreditation.id === accreditationToEdit.id
      );

      if (accreditationIndex === -1)
        throw new Error("Accreditation not found.");

      const { data } = await updateAccreditation(
        accreditationToEdit.id,
        formData
      );

      originalAccreditations[accreditationIndex] = data.data;

      setAccreditattions(originalAccreditations);

      setAccreditationToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Accreditation updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Accreditation not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setAccereditationToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteCampus = async () => {
    const originalAccreditations = [...accreditations];

    const newAccreditations = originalAccreditations.filter(
      (accreditation) => accreditation.id !== accreditationToDelete.id
    );

    setAccreditattions(newAccreditations);

    try {
      await deleteAccreditation(accreditationToDelete?.id);

      toast.success("Accreditation deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Accreditation not deleted, try again later"
        }`
      );

      setAccreditattions(originalAccreditations);
    } finally {
      setIsDeleteModalOpen(false);

      setAccereditationToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setAccreditationToEdit(null);
    setAccereditationToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 70,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Membership Status",
      dataIndex: "membership_status",
      key: "membership_status",
    },
    {
      title: "Accrediting Body",
      render: (_, record) => <p>{record?.accrediting_body?.name}</p>,
    },
    ...(!isInstAdmin
      ? [
          {
            title: "Institution",
            dataIndex: ["institution", "name"],
            key: "institution",
            render: (text) => (
              <span className="text-gray-700 text-sm">{text || "N/A"}</span>
            ),
          },
        ]
      : []),
    {
      title: "Date Awarded",
      dataIndex: "date_awarded",
      key: "date_awarded",
      render: (_, record) =>
        record.date_awarded && format(record.date_awarded, "dd/MM/yyyy"),
    },
    {
      title: "Date of Expiry",
      dataIndex: "expiry_date",
      key: "expiry_date",
      render: (_, record) =>
        record.expiry_date && format(record.expiry_date, "dd/MM/yyyy"),
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View Details">
            <button
              className="p-2 border border-blue-200 rounded-md text-blue-600 hover:bg-blue-50 transition"
              onClick={() => {
                setSelectedAccreditation(record);
                setIsDrawerOpen(true);
              }}
            >
              <BiShow size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Edit Accreditation">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Accreditation">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading
        title="Accreditation"
        subtitle1="Institution"
        subtitle2="Accreditation"
      />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Accreditation List"
            count={filteredAccreditations.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Accreditation"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredAccreditations.map((item) => ({
              ...item,
              key: item.id,
            }))}
            extra={
              <div className="mb-2 py-2 flex gap-2">
                {!isInstAdmin && (
                  <Select
                    multiple={false}
                    id="institutions"
                    defaultValue={selectedInstitution}
                    className="w-full h-[36px] min-w-[250px]"
                    onChange={(value) => {
                      setSelectedInstitution(value);
                    }}
                    showSearch={true}
                    filterOption={(input, option) =>
                      option?.children
                        ?.toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    <Select.Option value={"Not Selected"}>
                      Filter by Institution
                    </Select.Option>
                    {institutions &&
                      institutions.length > 0 &&
                      institutions.map((option) => (
                        <Select.Option key={option.id} value={option.id}>
                          {option.name}
                        </Select.Option>
                      ))}
                  </Select>
                )}

                <Select
                  multiple={false}
                  id="institutions"
                  defaultValue={selectedAccreditingBody}
                  className="w-full h-[36px] min-w-[250px]"
                  onChange={(value) => {
                    setSelectedAccreditingBody(value);
                  }}
                  showSearch={true}
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  <Select.Option value={"Not Selected"}>
                    Filter by Accrediting Body
                  </Select.Option>
                  {accreditingBodies &&
                    accreditingBodies.length > 0 &&
                    accreditingBodies.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.name}
                      </Select.Option>
                    ))}
                </Select>
              </div>
            }
          />

          {/* Modal */}

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {accreditationToEdit
                    ? "Edit Accreditation"
                    : "Add Accreditation"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            <Formik
              initialValues={{
                name: accreditationToEdit ? accreditationToEdit?.name : "",
                membership_status: accreditationToEdit
                  ? accreditationToEdit?.membership_status
                  : "",
                expiry_date: accreditationToEdit
                  ? accreditationToEdit?.expiry_date
                  : "",
                date_awarded: accreditationToEdit
                  ? accreditationToEdit?.date_awarded
                  : "",
                accrediting_body_id: accreditationToEdit
                  ? accreditationToEdit?.accrediting_body?.id
                  : "",

                institution_id: accreditationToEdit
                  ? accreditationToEdit?.institution?.id
                  : isInstAdmin
                  ? user?.institution?.id
                  : "",
              }}
              enableReinitialize
              validationSchema={accreditationSchema}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                submitForm,
                setFieldValue,
                setFieldTouched,
                touched,
              }) => {
                console.log(errors);

                return (
                  <Form className="space-y-5 px-4! py-3!">
                    {errs && <FormErrors errors={errs} />}

                    {isInstAdmin ? (
                      <input
                        type="hidden"
                        name="institution_id"
                        value={
                          accreditationToEdit
                            ? accreditationToEdit?.institution?.id
                            : user?.institution?.id
                        }
                      />
                    ) : (
                      <CustomDropDownInput
                        name="institution_id"
                        title="Institution"
                        placeholder="Select an institution"
                        requiredField
                        errors={errors}
                        values={values}
                        touched={touched}
                        displayLabel="name"
                        idLabel="id"
                        options={institutions}
                        setFieldValue={setFieldValue}
                      />
                    )}

                    <CustomDropDownInput
                      title="Accredting Body"
                      multipleSelection={false}
                      name="accrediting_body_id"
                      placeholder="Select Accrediting Body"
                      requiredField={true}
                      errors={errors}
                      options={accreditingBodies}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      values={values}
                      displayLabel="name"
                      setFieldValue={setFieldValue}
                      key="accrediting_body_id"
                      showSearch={true}
                    />

                    <TextInput
                      name="name"
                      title="Accreditation Name"
                      placeholder="Enter Accreditation Name"
                      requiredField={true}
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      autoFocus
                    />

                    <TextInput
                      name="membership_status"
                      title="Membership Status"
                      placeholder="Enter Membership Status"
                      requiredField={true}
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      autoFocus
                    />

                    <DateInput
                      name="expiry_date"
                      title="Expiry Date"
                      placeholder="YYYY-MM-DD"
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      defaultValue={"2025/01/01"}
                    />

                    <DateInput
                      name="date_awarded"
                      title="Date Awarded"
                      placeholder="YYYY-MM-DD"
                      requiredField={true}
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      defaultValue={"2025/01/01"}
                    />

                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteCampus}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />

          <Drawer
            title={
              <div className="text-lg font-semibold text-gray-700">
                {selectedAccreditation?.name || "Accreditation Details"}
              </div>
            }
            width={520}
            onClose={() => setIsDrawerOpen(false)}
            open={isDrawerOpen}
            placement="right"
          >
            {selectedAccreditation ? (
              <div className="text-gray-700 text-sm space-y-4">
                <div>
                  <strong>Institution:</strong>{" "}
                  {selectedAccreditation.institution?.name || "—"}
                </div>

                <div>
                  <strong>Accrediting Body:</strong>{" "}
                  {selectedAccreditation.accrediting_body?.name || "—"}
                </div>

                <div>
                  <strong>Membership Status:</strong>{" "}
                  {selectedAccreditation.membership_status || "—"}
                </div>

                <div>
                  <strong>Date Awarded:</strong>{" "}
                  {selectedAccreditation.date_awarded
                    ? format(selectedAccreditation.date_awarded, "dd/MM/yyyy")
                    : "—"}
                </div>

                <div>
                  <strong>Expiry Date:</strong>{" "}
                  {selectedAccreditation.expiry_date
                    ? format(selectedAccreditation.expiry_date, "dd/MM/yyyy")
                    : "—"}
                </div>
              </div>
            ) : (
              <p className="text-gray-500">No accreditation selected.</p>
            )}
          </Drawer>
        </div>
      </div>
    </>
  );
};

export default Accreditations;
